/*
Origin Core 1.2

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <cart-drawer-component> that manages the Shopify cart drawer UI.
It extends a dialog component, listens for cart add events to automatically open the drawer, and ensures the drawer closes when certain actions (like clicking the installments CTA) occur.
The component handles opening and closing the drawer and cleans up event listeners when disconnected.
It is registered as a custom element if not already defined.

*/

import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();

      if (event.detail?.data?.sections) {
        this.#updateCartContents(event.detail.data.sections);
      }
    }
  };

  #handleCartUpdate = (event) => {
    const { preventCartDrawer } = event.detail?.data || {};

    if (!preventCartDrawer && this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    this.dispatchEvent(new CustomEvent('cart-drawer:opened', {
      bubbles: true,
      detail: { timestamp: Date.now() }
    }));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Updates the cart contents with new HTML from sections
   * @param {Object} sections - The sections data from the cart response
   */
  #updateCartContents(sections) {
    try {
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (cartItemsComponent && cartItemsComponent.sectionId) {
        const sectionId = cartItemsComponent.sectionId;
        const newHtml = sections[sectionId];

        if (newHtml) {
          import('@theme/section-renderer').then(({ morphSection }) => {
            morphSection(sectionId, newHtml);
          }).catch(console.error);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
